import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EncryptionData, ErrorData, ErrorTypes, FieldsData, MessageActions } from '../../types';
import { encryptFieldsData, EPaymentMethod, parseFeesDisclaimers, updateFrameHeight } from '../../utils';
import { useHandleBroadcastChannel } from '../../hooks/useHandleBroadcastChannel';
import { bCService } from '../../events/broadcastChannel';
import { useTranslation } from 'react-i18next';
import { FieldViewProps } from '../types';


export const PayFieldView = ({ data, potentialCardType, error }: FieldViewProps) => {
  const { i18n } = useTranslation();
  const [search] = useSearchParams();
  const fieldType = search.get('field') ?? '';
  const clientId = search.get('clientId') ?? '';
  const sessionId = search.get('session') ?? '';
  const libInitializeId = search.get('libInitializeId') ?? '';
  const includeBIN = search.get('includeBIN') === 'true';
  const includeACHInfo = search.get('includeACHInfo') === 'true';

  const formValuesRef = useRef<Partial<FieldsData>>({});

  const publicKeyDataRef = useRef<EncryptionData | undefined>(data?.encryption);

  const updateHeight = () => updateFrameHeight(fieldType, sessionId, libInitializeId);

  const collectFormFields = async (bcData) => {
    if (!bcData.field
      || bcData.value === null
      || bcData.value === undefined
      || !bcData.fields) return;

    formValuesRef.current[bcData.field] = {
      isError: bcData.isError,
      data: bcData.isError ? bcData.error : bcData.value,
    };

    const collectedFormFieldsSet = new Set(Object.keys(formValuesRef.current));
    const isAllFields = bcData.fields.every((field: string) => collectedFormFieldsSet.has(field));

    if (!isAllFields) return;

    if (!publicKeyDataRef.current
      || !publicKeyDataRef.current.key
      || !publicKeyDataRef.current.keyId
    ) {
      window.parent.postMessage({
        action: MessageActions.FIELD_VALUE, 
        data: {
          isSuccess: false,
          errors: [{
            errorType: ErrorTypes.ENCRYPT_ERROR,
            errorText: 'Public key is missing',
          }],
        },
        sessionId,
        libInitializeId,
      }, '*');
      return;
    }

    const hasErrors = bcData.fields.some((field: string) => formValuesRef.current[field].isError);

    if (hasErrors) {
      const errors = Object.values(formValuesRef.current).reduce((accArr, fieldData) => {
        if (fieldData.isError && !!fieldData.data) {
          accArr.push(fieldData.data);
        }
        return accArr;
      }, [] as ErrorData[]);

      window.parent.postMessage({
        action: MessageActions.FIELD_VALUE,
        data: { isSuccess: false, errors },
        sessionId,
        libInitializeId,
      }, '*');
      formValuesRef.current = {};
      return;
    }

    const encryptedFieldsData = await encryptFieldsData(
      formValuesRef.current,
      publicKeyDataRef.current,
      includeBIN,
      includeACHInfo,
    );
    window.parent.postMessage({
      action: MessageActions.FIELD_VALUE,
      data: encryptedFieldsData,
      sessionId,
      libInitializeId,
    }, '*');

    formValuesRef.current = {};
  };

  const sendPermissions = (bcData) => {
    bCService.broadcastChannelServicePost({
      action: MessageActions.PERMISSIONS,
      field: bcData.field,
      data,
      clientId,
      potentialCardType,
    });
  };

  const hasSuitableFee = (paymentType: EPaymentMethod) => {
    const parsedClientFees = parseFeesDisclaimers(data?.clientFeesDisclaimers || []);
    const suitableClientFee = parsedClientFees
      .find((fee) => fee.paymentType?.toLowerCase() === paymentType);
    const hasClientFee = !!suitableClientFee
      && typeof suitableClientFee.disclaimer === 'object'
      && i18n.exists(suitableClientFee.disclaimer.key, { ns: 'fees' });

    const suitableFee = (data?.feesDisclaimers || [])
      .find((fee) => fee.paymentType?.toLowerCase() === paymentType);
    const hasFee = !!suitableFee && i18n.exists(suitableFee.key, { ns: 'feesPayPage' });

    return hasClientFee || hasFee;
  };

  const sendPermissionsToLibrary = () => {
    window.parent.postMessage({
      action: MessageActions.PAYMENT_METHOD,
      paymentMethod: data?.paymentMethod,
      clientFeesDisclaimers: {
        ach: hasSuitableFee(EPaymentMethod.ACH_ELECTRONIC_CHECK),
        credit: hasSuitableFee(EPaymentMethod.PAY_WITH_CREDIT_CARD),
      },
      error,
      sessionId,
      libInitializeId,
    }, '*');
  };

  useHandleBroadcastChannel({
    handleCollectFormFields: collectFormFields,
    handlePermissions: sendPermissions,
  }, sessionId, libInitializeId);

  useEffect(() => {
    sendPermissionsToLibrary();

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return <></>;
};
